import Icon, { IconProps } from "@ews/react-icons";

import
{
    AuthorizationLevel
} from "@ews/zlt-events";

import React, {
    createContext,
    FC,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect
} from "react";

import
{
    Polygon
} from 'geojson';

import
{
    StatusColor
} from "./Icons";

import
{
    useIonRouter
} from "@ionic/react";

import
{
    ReactDataClientError,
    useWebsocket
} from "./ReactData/ReactDataProvider";

import
{
    useMyDetails,
    useMyPermissions,
    useMyProfileId
} from "./Login/LoginProvider";

import
{
    useSystemDetails,
    useSystemPermissions,
    useSystemRTM,
    useSystemShareOperate
} from "./ReactData/system";

import
{
    systemInAuthorization,
    systemInReactSession
} from "@ews/zlt-operate";

import
{
    AuthorizeResponse,
    permissionEmpty,
    SystemStatus
} from "@ews/react-data";

import
{
    locationInArea,
    useGeoLocation
} from "./GeoLocation/GeoLocation";

import
{
    useInactiveTimeout
} from "./Interval";

import
{
    retrievePersistentData,
    usePersistentState
} from "./Persistence";

import
{
    useWLanSSID
} from "./WLanSSID/WLanSSID";

import
{
    useSystemStatus
} from "./System/SystemStatus";

import
{
    ImpersonationState
} from "./types";

type AcquireAuthorizationContextType = (level: AuthorizationLevel, code?: string) => Promise<boolean | any>;

export type AuthorizationContextType = {
    authorize: AcquireAuthorizationContextType,
    authorizationLevel: AuthorizationLevel;
};

const defaultAuthorizationContext: AuthorizationContextType = {
    authorize: async () => false,
    authorizationLevel: AuthorizationLevel.LEVEL1
};
const AuthorizationContext = createContext<AuthorizationContextType>(defaultAuthorizationContext);

export const useAuthorization = () =>
{
    return useContext(AuthorizationContext);
};

type AuthorizationStatusProps = Omit<IconProps, 'icon'>;

export const AuthorizationStatus: FC<AuthorizationStatusProps> = ({
    style = {},
    ...props
}) =>
{
    const { authorizationLevel } = useAuthorization();

    style = { ...style, ...{ "--fill-override": StatusColor.ONLINE } as React.CSSProperties };

    switch (authorizationLevel) {
        case AuthorizationLevel.LEVEL1:
            return <Icon icon="lock_icon_locked" {...props} style={style} />;
        case AuthorizationLevel.LEVEL2:
            return <Icon icon="lock_icon_unlocked2" {...props} style={style} />;
        case AuthorizationLevel.LEVEL3:
            return <Icon icon="lock_icon_unlocked3" {...props} style={style} />;
    }
};

export type AuthorizationProps = PropsWithChildren;

const permissionNotRequired = {};
const permissionPending = {};

type AuthorizationPermit = boolean | typeof permissionNotRequired;

const bufferTimer: Record<string, number> = {};

const useBufferedAuthorization = (
    systemId: string,
    authorizationType: string,
    permitted: AuthorizationPermit,
    bufferTimeout: number = 0,
) =>
{
    const [buffered, setBuffered] = usePersistentState<AuthorizationPermit>(`bufferedAuthorization:${authorizationType}`, permitted, systemId || "-");
    const clearTimeout = useCallback(() =>
    {
        window.clearTimeout(bufferTimer[authorizationType]);
        bufferTimer[authorizationType] = 0;
    }, [authorizationType]);

    useEffect(() =>
    {
        if (!systemId) {
            if (bufferTimer[authorizationType]) {
                clearTimeout();
            }
            return setBuffered(false);
        }

        if (!permitted && buffered === true) {
            if (!bufferTimer[authorizationType]) {
                bufferTimer[authorizationType] = window.setTimeout(() =>
                {
                    clearTimeout();
                    setBuffered(permitted);
                }, bufferTimeout);
            }
        } else {
            if (bufferTimer[authorizationType]) {
                clearTimeout();
            }
            setBuffered(permitted);
        }

    }, [
        authorizationType,
        permitted,
        buffered,
        bufferTimeout,
        setBuffered,
        clearTimeout,
        systemId
    ]);

    return buffered;
};

const useAuthorizationPermitBySystemAuthorization = (
    systemId: string,
    permissionPendingValue: AuthorizationPermit = false
): AuthorizationPermit =>
{
    const me = useMyProfileId();
    const systemPermissions = useSystemShareOperate(systemId, me);
    const rtm = useSystemRTM(systemId);

    if ("restrictOperationToAuthorizedUsers" in systemPermissions) {
        if (systemPermissions.restrictOperationToAuthorizedUsers) return systemInAuthorization(rtm.map(r => r.eventKindType));
        return permissionNotRequired;
    }

    return permissionPendingValue;
};
export const AuthorizationSystemAuthorization: FC<AuthorizationStatusProps> = ({
    style = {},
    ...props
}) =>
{
    const systemId = useCurrentSystemId();
    const authorized = useAuthorizationPermitBySystemAuthorization(systemId, permissionNotRequired);

    if (authorized === permissionNotRequired) return <></>;
    style = { ...style, ...{ "--fill-override": authorized ? StatusColor.ONLINE : StatusColor.OFFLINE } as React.CSSProperties };

    return <Icon icon="bmz_body_lck_aktiv" style={style} {...props} />;
};

const useAuthorizationPermitByWLanSSID = (
    systemId: string,
    permissionPendingValue: AuthorizationPermit = false
): AuthorizationPermit =>
{
    const me = useMyProfileId();
    const systemPermissions = useSystemPermissions(systemId);
    const systemSharePermissions = useSystemShareOperate(systemId, me);
    const SSID = useWLanSSID(systemPermissions.restrictOperationToWlanSSID ? 5000 : 0);

    if ("restrictOperationToWlanSSID" in systemSharePermissions && "WlanSSID" in systemPermissions) {
        if (systemSharePermissions.restrictOperationToWlanSSID && systemPermissions.WlanSSID) {
            return SSID ? systemPermissions.WlanSSID === SSID : false;
        } else {
            return permissionNotRequired;
        }
    }

    return permissionPendingValue;
};

const useAuthorizationPermitByWLanSSIDBuffered = (
    systemId: string,
    permissionPendingValue: AuthorizationPermit = false
) =>
{
    const systemPermissions = useSystemPermissions(systemId);
    const permitted = useAuthorizationPermitByWLanSSID(systemId, permissionPending);

    const buffered = useBufferedAuthorization(systemId, "authorizationPermitByWLanSSID", permitted === permissionPending ? false : permitted, minutesToTimeout(systemPermissions.bufferGPSInAreaTime));

    if (permitted === permissionPending) return permissionPendingValue;
    if (permitted === permissionNotRequired) return permitted;

    return buffered;
};

export const AuthorizationWLanSSID: FC<AuthorizationStatusProps> = ({
    style = {},
    ...props
}) =>
{
    const systemId = useCurrentSystemId();
    const authorized = useAuthorizationPermitByWLanSSIDBuffered(systemId, permissionNotRequired);

    if (authorized === permissionNotRequired) return <></>;
    style = { ...style, ...{ "--fill-override": authorized ? StatusColor.ONLINE : StatusColor.OFFLINE } as React.CSSProperties };

    return <Icon icon="wlan" style={style} {...props} />;
};

const useAuthorizationPermitByGeoCoordinate = (
    systemId: string,
    permissionPendingValue: AuthorizationPermit = false
): AuthorizationPermit =>
{
    const me = useMyProfileId();
    const systemPermissions = useSystemShareOperate(systemId, me);
    const systemDetails = useSystemDetails(systemId);
    const geoLocation = useGeoLocation(systemPermissions.restrictOperationToGPSArea ? 5000 : 0);

    if ("restrictOperationToGPSArea" in systemPermissions && "GPSArea" in systemDetails) {
        if (systemPermissions.restrictOperationToGPSArea && systemDetails.GPSArea) {
            return geoLocation ? locationInArea(geoLocation, systemDetails.GPSArea as Polygon) : permissionPendingValue;
        } else {
            return permissionNotRequired;
        }
    }

    return permissionPendingValue;

};

const useAuthorizationPermitByGeoCoordinateBuffered = (
    systemId: string,
    permissionPendingValue: AuthorizationPermit = false
) =>
{
    const systemPermissions = useSystemPermissions(systemId);
    const permitted = useAuthorizationPermitByGeoCoordinate(systemId, permissionPending);
    const buffered = useBufferedAuthorization(systemId, "authorizationPermitByGeoCoordinate", permitted === permissionPending ? false : permitted, minutesToTimeout(systemPermissions.bufferGPSInAreaTime));

    if (permitted === permissionPending) return permissionPendingValue;
    if (permitted === permissionNotRequired) return permitted;

    return buffered;
};

export const AuthorizationGeoCoordinate: FC<AuthorizationStatusProps> = ({
    style = {},
    ...props
}) =>
{
    const systemId = useCurrentSystemId();
    const authorized = useAuthorizationPermitByGeoCoordinateBuffered(systemId, permissionNotRequired);

    if (authorized === permissionNotRequired) return <></>;
    style = { ...style, ...{ "--fill-override": authorized ? StatusColor.ONLINE : StatusColor.OFFLINE } as React.CSSProperties };

    return <Icon icon="location" style={style} {...props} />;
};

const useAuthorizationPermitByReactSession = (
    systemId: string,
    permissionPendingValue: AuthorizationPermit = false
): AuthorizationPermit =>
{
    const userPermissions = useMyPermissions();
    const me = useMyProfileId();
    //const systemPermissions = useSystemPermissions(systemId);
    const systemPermissions = useSystemShareOperate(systemId, me);
    const rtm = useSystemRTM(systemId);

    if ("restrictOperationToApprovedSession" in userPermissions && "restrictOperationToApprovedSession" in systemPermissions) {

        if (
            permissionEmpty(userPermissions.restrictOperationToApprovedSession!) &&
            !systemPermissions.restrictOperationToApprovedSession
        ) return permissionNotRequired;

        return systemInReactSession(rtm.map(r => r.eventKindType));
    }
    return permissionPendingValue;
};

export const AuthorizationReactSession: FC<AuthorizationStatusProps> = ({
    style = {},
    ...props
}) =>
{
    const systemId = useCurrentSystemId();
    const authorized = useAuthorizationPermitByReactSession(systemId, permissionNotRequired);


    if (authorized === permissionNotRequired) return <></>;
    style = { ...style, ...{ "--fill-override": authorized ? StatusColor.ONLINE : StatusColor.OFFLINE } as React.CSSProperties };

    return <Icon icon="user_session" style={style} {...props} />;
};

// export const useAuthorizationPermitBySystemDisplay = (systemId: string) =>
// {
//     const me = useMyProfileId();
//     const systemDisplay = useUserSystemDisplay(me);
//     const authorizationPermitted = useSystemDisplayFilter(systemId);

//     const hasFilter = ["displayCategory1", "displayCategory2", "displayCategory3", "displayCategory4", "displayCategory5"].filter(category => systemDisplay[category as keyof UserSystemDisplay]).length;
//     return hasFilter ? authorizationPermitted : permissionNotRequired;
// };

//let redirectTimeout: number;

export const usePermitAuthorization = (systemId: string): boolean =>
{

    const systemStatus = useSystemStatus(systemId);
    const reactSession = useAuthorizationPermitByReactSession(systemId);
    const systemAuthorized = useAuthorizationPermitBySystemAuthorization(systemId);
    const gpsAuthorized = useAuthorizationPermitByGeoCoordinateBuffered(systemId);
    const wLanAuthorized = useAuthorizationPermitByWLanSSIDBuffered(systemId);

    const isOnline = systemStatus === SystemStatus.ONLINE;
    const buffered = useBufferedAuthorization(
        systemId,
        "authorizationPermit",
        !!(reactSession && systemAuthorized && gpsAuthorized && wLanAuthorized && isOnline),
        1000
    );

    return Boolean(buffered); // && displayFilterAuthorized;
};

export const useCurrentSystemId = () =>
{
    const router = useIonRouter();
    const route = router.routeInfo.pathname.match(/^\/(?<locale>[^/]+)\/system\/(?<systemId>[^/]+).*/);
    return route?.groups?.systemId || "";
};

const minutesToTimeout = (minutes: number = 0) => minutes * 60 * 1000;

export const AuthorizationPlan: FC<AuthorizationStatusProps> = ({
    style = {},
    ...props
}) =>
{
    const [planEdit] = usePersistentState<boolean>("authorizationLevelPlan");

    if (planEdit) {
        return <Icon icon="icon_Plan_unlocked"
            style={{ ...style, ...{ "--fill-override": StatusColor.ONLINE } }}
            {...props} />;
    }
    return <></>;
};


export const Authorization: FC<AuthorizationProps> = ({
    children
}) =>
{
    const systemId = useCurrentSystemId();
    const { timerAuthorization } = useMyDetails();

    const authorizationPermitted = usePermitAuthorization(systemId);
    const [authorization, setAuthorization] = usePersistentState<{
        level: number,
        code?: string;
    }>("authorizationLevel", { level: AuthorizationLevel.LEVEL1 }, systemId || "-");

    const authorizationLevel = authorization.level;

    useEffect(() =>
    {
        if (!authorizationPermitted) {
            setAuthorization(() => ({ level: AuthorizationLevel.LEVEL1 }));
        }
    }, [
        authorizationPermitted,
        setAuthorization
    ]);

    const authClient = useWebsocket();

    useInactiveTimeout(() =>
    {
        setAuthorization({ level: AuthorizationLevel.LEVEL1 });
    }, authorizationLevel === AuthorizationLevel.LEVEL1 ? 0 : minutesToTimeout(timerAuthorization));

    useEffect(() =>
    {
        const unregisterAuthorization = (e?: any) =>
        {
            //@ts-ignore
            authClient.auth({
                type: "authorize",
            });
        };

        if (authorizationLevel === AuthorizationLevel.LEVEL1) {
            unregisterAuthorization();
        } else if (authorization.code) {
            authClient.auth({
                type: "authorize",
                systemId,
                level: authorizationLevel,
                code: authorization.code
            });
        }

        window.addEventListener("beforeunload", unregisterAuthorization);
        return () =>
        {
            window.removeEventListener("beforeunload", unregisterAuthorization);
        };
    }, [authClient, authorization, authorizationLevel, systemId]);

    const authorize: AcquireAuthorizationContextType = async (level: AuthorizationLevel, code?: string) =>
    {
        if (level === AuthorizationLevel.LEVEL1) {

            setAuthorization({ level, code });
            return true;
        }

        if (!code) return false;

        try {

            const response = await authClient.auth({
                type: "authorize",
                systemId,
                level,
                code
            });

            if (!('success' in response)) return false;

            const data = response.success as AuthorizeResponse;

            if (!("level" in data)) return false;

            if (data.level !== level) {
                return data.currentAuthorization || false;
            }

            setAuthorization({ level: data.level, code });
            return true;

        } catch (e) {
            const error = typeof e === "string" ? (JSON.parse(e as string) as ReactDataClientError).body.error : (e as Error).message;;
            setAuthorization({ level: AuthorizationLevel.LEVEL1 });
            throw error;
        }


    };

    const authorizationContext: AuthorizationContextType = { authorize, authorizationLevel };
    return (
        <AuthorizationContext.Provider value={authorizationContext}>
            {children}
        </AuthorizationContext.Provider>

    );
};

export const isUserImpersonated = () => (retrievePersistentData('state: impersonationState', localStorage)?.data as ImpersonationState)?.impersonationStatus === 'active';
